import React, { useState } from 'react';
import { format } from 'date-fns';
import { upperFirst, lowerCase } from 'lodash';
import { useParams } from 'react-router-dom';
import { ClipboardIcon, PencilIcon } from '@heroicons/react/24/outline';
import 'react-table/react-table.css';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { toast } from '@dashboard/context/ToastContext';

import checkIsConnectedToStripe from '@dashboard/services/seller/checkIsConnectedToStripe';
import { fetcher } from '@dashboard/services/request';
import { Label } from '@dashboard/components/Form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@dashboard/components/ui/select';
import { Switch } from '@dashboard/components/ui/switch';
import { Badge } from '@dashboard/components/ui/badge';
import Dialog from '@dashboard/components/Dialog';
import { Input } from '@dashboard/components/ui/input';
import { Checkbox } from '@dashboard/components/ui/checkbox';
import { InfoTooltip } from '@dashboard/components/Tooltip';
import { useMerchant } from '@dashboard/hooks/queries/useAdminQueries';
import {
  useUpdateMerchant,
  useUpdateMerchantIndustry,
  useDisableSeller,
  useEnableSeller,
  useCancelSubscription,
  useExpireTrial,
  useExtendTrial,
  useCompSeller,
  useRemoveComp,
  useStartStatisticsAggregate,
  useStartMetricsAggregation,
} from '@dashboard/hooks/queries/useAdminMutations';
import Link from '@dashboard/components/Link/Link';
import { IndustryEnum } from '@dashboard/enums/industry';
import getStripeDashboardUrl from '@dashboard/lib/stripe/get-stripe-dashboard-url';
import { formatPriceInput, formatPriceForInput } from '@dashboard/services/formatting/price';
import { formatPrice } from '@dashboard/services/formatting';

import { METRICS } from '@dashboard/scenes/Dashboard/metrics';
import { dateRanges } from '@dashboard/scenes/Dashboard/date-ranges';
import { getBillingPeriod } from '@dashboard/services/plans';

import Button from '@dashboard/components/Button';
import CopyTextWithTooltip from '@dashboard/components/CopyTextWithTooltip';
import { DescriptionList } from '@dashboard/components/DescriptionList';

import Circle from '../components/Circle';
import {
  formatContractDate,
  fromContractDateInput,
  getContractEndStatus,
  toContractDateInput,
} from './merchantUtils';
import TeamMembersTable from '../components/TeamMembersTable';
import MerchantBillingHistory from '../components/MerchantBillingHistory';
import StripeConnectionHistory from '../components/StripeConnectionHistory';
import StripeAccountStatus from '../components/StripeAccountStatus';
import OAuthConnectionHistory from '../components/OAuthConnectionHistory';
import MerchantAiSection from './AiUsage/MerchantAiSection';
import { AdminRolesEnum } from '@/dashboard/enums/admin-roles';
import useViewAsMerchant from '@dashboard/admin/hooks/useViewAsMerchant';
import { confirm } from '@dashboard/lib/confirm';
import { prompt } from '@dashboard/lib/prompt';
import { Form } from '@/dashboard/components/Formik';
import { getRenewalStatus } from './merchantUtils';

const AffiliateBadgeFields = ({ seller, updateSellerMutation }) => {
  const [token, setToken] = useState(seller.affiliateBadge?.token || '');
  const [enabled, setEnabled] = useState(seller.affiliateBadge?.enabled || false);

  const isDirty =
    token !== (seller.affiliateBadge?.token || '') ||
    enabled !== (seller.affiliateBadge?.enabled || false);

  return (
    <div className="flex items-center gap-x-3">
      <Input
        value={token}
        onChange={e => setToken(e.target.value)}
        placeholder="Rewardful token"
        className="max-w-44"
      />

      <label className="flex items-center gap-x-1.5 whitespace-nowrap cursor-pointer">
        <Checkbox checked={enabled} onCheckedChange={checked => setEnabled(checked === true)} />
        Enabled
      </label>

      <Button
        appearance="secondary"
        size="small"
        disabled={!isDirty || updateSellerMutation.isPending}
        handleClick={() =>
          updateSellerMutation.mutate({
            sellerId: seller._id,
            data: { affiliateBadge: { token: token.trim(), enabled } },
          })
        }
      >
        Save
      </Button>
    </div>
  );
};

const CompDialog = ({ seller, sellerId, compSellerMutation, removeCompMutation }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [compPlan, setCompPlan] = useState('scale:10000');
  const [reason, setReason] = useState('');

  const isComped = seller.subscriptionStatus === 'comped';

  const close = () => setIsOpen(false);

  return (
    <>
      <Button appearance="secondary" size="small" handleClick={() => setIsOpen(true)}>
        {isComped ? 'Manage comp' : 'Comp subscription'}
      </Button>

      <Dialog
        open={isOpen}
        onCancel={close}
        size="md"
        title={isComped ? 'Manage comp' : 'Comp subscription'}
        description={
          isComped
            ? 'Removing the comp moves this merchant to Free and emails them the downgrade confirmation.'
            : 'Grants a paid plan with no Stripe subscription behind it. No card, no invoices, and they stay out of usage limits and customer counts.'
        }
        cancelButton={{ label: 'Cancel', appearance: 'link' }}
        confirmButton={{
          label: isComped ? 'Remove comp' : 'Comp plan',
          appearance: 'primary',
          disabled: isComped ? removeCompMutation.isPending : compSellerMutation.isPending,
          onClick: () =>
            isComped
              ? removeCompMutation.mutate({ sellerId }, { onSuccess: close })
              : compSellerMutation.mutate(
                  {
                    sellerId,
                    planHandle: compPlan.split(':')[0],
                    ...(compPlan.includes(':') && { bracket: Number(compPlan.split(':')[1]) }),
                    ...(compPlan === 'unlimited' && { billingPeriod: 'year' }),
                    reason: reason || undefined,
                  },
                  { onSuccess: close },
                ),
        }}
      >
        {isComped ? (
          <DescriptionList.List>
            <DescriptionList.Item>
              <DescriptionList.Term>Plan</DescriptionList.Term>
              <DescriptionList.Description>{seller.plan?.title}</DescriptionList.Description>
            </DescriptionList.Item>
            <DescriptionList.Item>
              <DescriptionList.Term>Reason</DescriptionList.Term>
              <DescriptionList.Description>
                {seller.comp?.reason || 'Not recorded'}
              </DescriptionList.Description>
            </DescriptionList.Item>
            <DescriptionList.Item>
              <DescriptionList.Term>Comped on</DescriptionList.Term>
              <DescriptionList.Description>
                {seller.comp?.grantedAt
                  ? format(new Date(seller.comp.grantedAt), 'd MMM, yyyy')
                  : 'Not recorded'}
              </DescriptionList.Description>
            </DescriptionList.Item>
          </DescriptionList.List>
        ) : (
          <div className="grid grid-cols-3 gap-x-3">
            <div>
              <Label htmlFor="compPlan">Plan</Label>
              <Select value={compPlan} onValueChange={setCompPlan}>
                <SelectTrigger id="compPlan" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="launch">Launch</SelectItem>
                  <SelectItem value="scale:10000">Scale ($10K)</SelectItem>
                  <SelectItem value="scale">Scale</SelectItem>
                  <SelectItem value="unlimited">Unlimited</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="col-span-2">
              <Label htmlFor="compReason">Reason</Label>
              <Input
                id="compReason"
                value={reason}
                onChange={event => setReason(event.target.value)}
                placeholder="e.g. Framer design partner"
              />
            </div>
          </div>
        )}
      </Dialog>
    </>
  );
};

const Merchant = () => {
  const { token, user: adminUser } = useAuth();
  const { sellerId } = useParams();
  const [isEditingIndustry, setIsEditingIndustry] = useState(false);
  const [isEditingCustomPricing, setIsEditingCustomPricing] = useState(false);
  const handleLogAdminInAsSeller = useViewAsMerchant();

  const { data: seller, isLoading } = useMerchant(sellerId);
  const hasCustomPricing = !!(
    seller?.plan?.volumeLimit ||
    seller?.plan?.customPrice ||
    seller?.plan?.contractEndDate
  );
  const contractEndStatus = getContractEndStatus(seller?.plan?.contractEndDate);

  const setMerchantIndustryMutation = useUpdateMerchantIndustry({
    onSuccess: () => {
      toast({
        title: 'Merchant industry updated',
      });
      setIsEditingIndustry(false);
    },
  });

  const disableSellerMutation = useDisableSeller({
    onSuccess: (_, { notify }) => {
      toast({
        title: 'Seller disabled',
        description: notify
          ? 'The seller has been disabled and notified via email.'
          : 'The seller has been disabled.',
      });
    },
  });

  const enableSellerMutation = useEnableSeller({
    onSuccess: () => {
      toast({
        title: 'Seller enabled',
      });
    },
  });

  const updateSellerMutation = useUpdateMerchant({
    onSuccess: () => {
      toast({
        title: 'Seller updated',
      });
    },
  });

  const cancelSubscriptionMutation = useCancelSubscription({
    onSuccess: () => {
      toast({
        title: 'Subscription cancelled',
      });
    },
    onError: error => {
      window.alert(error.message);
    },
  });

  const expireTrialMutation = useExpireTrial({
    onSuccess: () => {
      toast({
        title: 'Trial expired',
      });
    },
    onError: error => {
      window.alert(error.message);
    },
  });

  const [extendTrialDays, setExtendTrialDays] = useState(1);

  const extendTrialMutation = useExtendTrial({
    onSuccess: () => {
      toast({
        title: 'Trial extended',
      });
    },
    onError: error => {
      window.alert(error.message);
    },
  });

  const compSellerMutation = useCompSeller({
    onSuccess: () => {
      toast({
        title: 'Plan comped',
      });
    },
    onError: error => {
      toast({
        title: 'Could not comp this plan',
        description: error?.message,
        type: 'error',
      });
    },
  });

  const removeCompMutation = useRemoveComp({
    onSuccess: () => {
      toast({
        title: 'Comp removed, merchant is on Free',
      });
    },
    onError: error => {
      toast({
        title: 'Could not remove the comp',
        description: error?.message,
        type: 'error',
      });
    },
  });

  const [statisticsRange, setStatisticsRange] = React.useState('lastHour');
  const [metric, setMetric] = React.useState('');

  const startStatisticsAggregate = useStartStatisticsAggregate({
    onSuccess: () => {
      toast({
        title: 'Statistics aggregation started',
        type: 'success',
      });
    },
    onError: error => {
      toast({
        title: 'Error starting statistics aggregation',
        description: error?.message,
        type: 'error',
      });
    },
  });

  const [metricsRange, setMetricsRange] = React.useState('lastHour');

  const startMetricsAggregation = useStartMetricsAggregation({
    onSuccess: () => {
      toast({
        title: 'Metrics aggregation started',
      });
    },
    onError: error => {
      toast({
        title: 'Error starting metrics aggregation',
        description: error?.message,
        type: 'error',
      });
    },
  });

  async function importStripeSubscriptions(sellerId) {
    // confirm actions
    const confirmed = await confirm(
      "This will import this merchant's Stripe subscriptions and collect fees on them. Only do this after a merchant has agreed. Are you sure you want to continue?",
    );

    if (!confirmed) {
      return;
    }

    const { data } = await fetcher(
      `/admin/sellers/${sellerId}/import-stripe-subscriptions`,
      token,
      { method: 'POST' },
    );

    return data;
  }

  async function disableSeller(sellerId) {
    // confirm actions
    const result = await prompt(
      'Are you sure you want to disable this seller? They will no longer be able to log in. Enter a reason:',
      {
        placeholder: 'Reason',
        checkbox: {
          label: 'Send email to seller',
          name: 'notify',
          defaultChecked: false,
        },
      },
    );

    if (!result || !result.value) {
      return;
    }

    disableSellerMutation.mutate({ sellerId, reason: result.value, notify: result.notify });
  }

  async function enableSeller(sellerId) {
    // confirm actions
    const confirmed = await confirm(
      'Are you sure you want to enable this seller? They will be able to log in again.',
    );

    if (!confirmed) {
      return;
    }

    enableSellerMutation.mutate({ sellerId });
  }

  if (!seller) {
    if (isLoading) {
      return 'Loading...';
    } else {
      return 'Not found';
    }
  }

  const sellerOwner = seller.users.find(u => u.role === 'owner')?.user;
  const sellerOwnerEmail = sellerOwner?.email;
  const crispSellerProfileUrl = `https://app.crisp.chat/website/abe8e9e1-02b6-4715-b289-7573fd280b36/contacts?query=${sellerOwnerEmail}`;

  return (
    <div className="mt-8 space-y-8">
      {seller.disabled && (
        <div className="col-span-2">
          <div className="bg-red-50 dark:bg-red-500/15 border border-red-200 dark:border-red-500/30 text-red-500 px-4 py-3 rounded relative">
            <strong className="font-bold">Merchant is disabled.</strong>
            <span className="block sm:inline"> Reason: {seller.disabledReason}</span>
          </div>
        </div>
      )}

      <div className="flex gap-x-4">
        <Button handleClick={() => handleLogAdminInAsSeller(seller._id, '/')} appearance="primary">
          View dashboard
        </Button>

        <Button
          handleClick={() => handleLogAdminInAsSeller(seller._id, '/pages')}
          appearance="primary"
        >
          View pages
        </Button>

        <Button href={crispSellerProfileUrl} appearance="primary" target="_blank" external>
          Open in Crisp
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-16">
        <div>
          <h1 className="mb-4">{seller.displayName}</h1>
          <div className="max-w-screen-sm">
            <DescriptionList.List>
              <DescriptionList.Item>
                <DescriptionList.Term>Store name</DescriptionList.Term>
                <DescriptionList.Description>
                  {seller.displayName || 'N/A'}
                </DescriptionList.Description>
              </DescriptionList.Item>

              <DescriptionList.Item>
                <DescriptionList.Term>Store focus</DescriptionList.Term>
                <DescriptionList.Description>
                  {isEditingIndustry ? (
                    <Select
                      value={seller.industry || undefined}
                      onValueChange={industry =>
                        setMerchantIndustryMutation.mutate({
                          sellerId: seller._id,
                          industry,
                        })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Industry" />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.values(IndustryEnum).map(industry => (
                          <SelectItem key={industry} value={industry}>
                            {upperFirst(industry.replace(/-/g, ' '))}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div>
                      <span className="mr-2">
                        {seller.industry ? upperFirst(seller.industry.replace(/-/g, ' ')) : 'N/A'}
                      </span>
                      <Button appearance="icon-button" onClick={() => setIsEditingIndustry(true)}>
                        <PencilIcon className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </DescriptionList.Description>
              </DescriptionList.Item>

              <DescriptionList.Item>
                <DescriptionList.Term>Website</DescriptionList.Term>
                <DescriptionList.Description>
                  <Link href={seller.url} blank>
                    {seller.url}
                  </Link>
                </DescriptionList.Description>
              </DescriptionList.Item>

              <DescriptionList.Item>
                <DescriptionList.Term>Signed up at</DescriptionList.Term>
                <DescriptionList.Description>
                  {format(new Date(seller.createdAt), 'd MMM, yyyy, HH:mm')}
                </DescriptionList.Description>
              </DescriptionList.Item>

              <DescriptionList.Item>
                <DescriptionList.Term>Last active at</DescriptionList.Term>
                <DescriptionList.Description>
                  {sellerOwner?.lastActivity
                    ? format(new Date(sellerOwner.lastActivity), 'd MMM, yyyy, HH:mm')
                    : 'N/A'}
                </DescriptionList.Description>
              </DescriptionList.Item>

              <DescriptionList.Item>
                <DescriptionList.Term>Store ID</DescriptionList.Term>
                <DescriptionList.Description>
                  <CopyTextWithTooltip text={seller._id}>
                    <div className="flex gap-x-1 items-center">
                      <span>{seller._id}</span>
                      <ClipboardIcon className="w-4 h-4" />
                    </div>
                  </CopyTextWithTooltip>
                </DescriptionList.Description>
              </DescriptionList.Item>

              <DescriptionList.Item>
                <DescriptionList.Term>Stripe Account</DescriptionList.Term>
                <DescriptionList.Description>
                  <div className="flex items-center gap-x-2">
                    <Circle color={checkIsConnectedToStripe(seller) ? 'green' : 'red'} />
                    Connected to Stripe
                  </div>

                  <StripeAccountStatus seller={seller} />

                  {seller.paymentGateways?.stripe?.userId && (
                    <>
                      <CopyTextWithTooltip text={seller.paymentGateways?.stripe?.userId}>
                        <div className="flex gap-x-1 items-center">
                          <span>{seller.paymentGateways?.stripe?.userId}</span>
                          <ClipboardIcon className="w-4 h-4" />
                        </div>
                      </CopyTextWithTooltip>

                      <br />
                      <Button
                        appearance="light"
                        to={getStripeDashboardUrl(
                          `/connect/accounts/${seller.paymentGateways.stripe.userId}/activity`,
                          { isConnectAccountView: true },
                        )}
                        target="_blank"
                        size="small"
                      >
                        View Stripe Connect Account
                      </Button>
                    </>
                  )}
                </DescriptionList.Description>
              </DescriptionList.Item>

              <DescriptionList.Item>
                <DescriptionList.Term>Stripe connection history</DescriptionList.Term>
                <DescriptionList.Description>
                  <StripeConnectionHistory history={seller.stripeConnectionHistory} />
                </DescriptionList.Description>
              </DescriptionList.Item>

              <DescriptionList.Item>
                <DescriptionList.Term>Integration connection history</DescriptionList.Term>
                <DescriptionList.Description>
                  <OAuthConnectionHistory history={seller.oauthConnectionHistory} />
                </DescriptionList.Description>
              </DescriptionList.Item>

              <DescriptionList.Item>
                <DescriptionList.Term>Stripe Customer</DescriptionList.Term>
                <DescriptionList.Description>
                  {seller.stripeCustomerId ? (
                    <CopyTextWithTooltip text={seller.stripeCustomerId}>
                      <div className="flex gap-x-1 items-center">
                        <span>{seller.stripeCustomerId}</span>
                        <ClipboardIcon className="w-4 h-4" />
                      </div>
                    </CopyTextWithTooltip>
                  ) : (
                    'N/A'
                  )}

                  {seller.stripeCustomerId && (
                    <>
                      <br />
                      <Button
                        appearance="light"
                        to={getStripeDashboardUrl(`/customers/${seller.stripeCustomerId}`)}
                        target="_blank"
                        size="small"
                      >
                        View Stripe Customer
                      </Button>
                    </>
                  )}
                </DescriptionList.Description>
              </DescriptionList.Item>

              <DescriptionList.Item>
                <DescriptionList.Term>Postmark Server</DescriptionList.Term>
                <DescriptionList.Description>
                  {seller.senderServer ? (
                    <Button
                      href={`https://account.postmarkapp.com/servers/${seller.senderServer?.id}/streams/outbound/events`}
                      target="_blank"
                      appearance="secondary"
                      size="small"
                    >
                      View Postmark Server
                    </Button>
                  ) : (
                    'N/A'
                  )}
                </DescriptionList.Description>
              </DescriptionList.Item>

              <DescriptionList.Item>
                <DescriptionList.Term>
                  Affiliate badge
                  <InfoTooltip>
                    {'Links the "Powered by Checkout Page" badge on this seller\'s checkouts, ' +
                      "customer portal and store emails to the partner's Rewardful affiliate " +
                      'URL, so signups coming from the badge are credited to the partner.'}
                  </InfoTooltip>
                </DescriptionList.Term>
                <DescriptionList.Description>
                  <AffiliateBadgeFields
                    seller={seller}
                    updateSellerMutation={updateSellerMutation}
                  />
                </DescriptionList.Description>
              </DescriptionList.Item>
            </DescriptionList.List>
          </div>
        </div>

        <div className="space-y-8">
          <div className="space-y-4">
            <h2 className="mb-4">Subscription</h2>
            <div className="flex flex-col items-start gap-y-2">
              <div className="flex items-center gap-x-2">
                <Circle
                  color={
                    seller.subscriptionStatus === 'trialing' ||
                    seller.stripeSubscriptionStatus === 'past_due'
                      ? 'orange'
                      : seller.subscriptionStatus === 'active' ||
                          seller.subscriptionStatus === 'comped'
                        ? 'green'
                        : 'red'
                  }
                />
                {seller.plan?.title}
                {seller.plan?.handle === 'scale' && seller.plan?.bracket && (
                  <span> ${seller.plan.bracket / 1000}K</span>
                )}{' '}
                {getBillingPeriod(seller.planBillingPeriod)} ({getRenewalStatus(seller)})
              </div>
            </div>

            {seller.subscriptionStatus === 'comped' && (
              <div className="max-w-lg mb-4">
                <p className="text-sm text-gray-500 dark:text-muted-foreground mb-1">
                  This plan is comped
                  {seller.comp?.reason ? `: ${seller.comp.reason}` : '.'}
                </p>
                <ul className="list-disc ml-5 text-sm text-gray-500 dark:text-muted-foreground">
                  <li>
                    Comped on:{' '}
                    {seller.comp?.grantedAt
                      ? format(new Date(seller.comp.grantedAt), 'd MMM, yyyy, HH:mm')
                      : 'Not recorded'}
                  </li>
                  <li>No Stripe subscription, so no invoices and nothing to cancel.</li>
                  <li>Excluded from usage limits, overage fees and active customer counts.</li>
                </ul>
              </div>
            )}

            {hasCustomPricing && (
              <div className="max-w-lg mb-4">
                <p className="text-sm text-gray-500 dark:text-muted-foreground mb-1">
                  This merchant has a custom pricing agreement.
                </p>
                <ul className="list-disc ml-5 text-sm text-gray-500 dark:text-muted-foreground">
                  <li>
                    Volume limit:{' '}
                    {seller.plan.volumeLimit
                      ? formatPrice({
                          amount: seller.plan.volumeLimit,
                          currency: 'usd',
                        })
                      : 'Not set'}
                  </li>
                  <li>
                    Custom price:{' '}
                    {seller.plan.customPrice
                      ? formatPrice({
                          amount: seller.plan.customPrice,
                          currency: 'usd',
                        })
                      : 'Not set'}
                  </li>
                  <li>
                    Plan renews on:{' '}
                    {seller.stripeSubscriptionCurrentPeriodEnd
                      ? format(
                          new Date(seller.stripeSubscriptionCurrentPeriodEnd),
                          'd MMM, yyyy, HH:mm',
                        )
                      : 'Not set'}
                  </li>
                  <li>Contract starts: {formatContractDate(seller.plan.contractStartDate)}</li>
                  <li>
                    Contract ends: {formatContractDate(seller.plan.contractEndDate)}
                    {contractEndStatus && (
                      <Badge variant={contractEndStatus.variant} className="ml-2">
                        {contractEndStatus.label}
                      </Badge>
                    )}
                  </li>
                </ul>
              </div>
            )}

            {adminUser.role === AdminRolesEnum.ADMIN && (
              <div className="max-w-lg">
                <div className="flex items-center justify-between gap-x-4">
                  <Label
                    htmlFor="editCustomPricing"
                    className="font-semibold text-gray-800 dark:text-foreground !mb-0"
                  >
                    Edit custom pricing agreement
                  </Label>
                  <Switch
                    id="editCustomPricing"
                    checked={isEditingCustomPricing}
                    onCheckedChange={setIsEditingCustomPricing}
                  />
                </div>

                {isEditingCustomPricing && (
                  <div className="mt-2">
                    <Form
                      initialValues={{
                        plan: {
                          volumeLimit: seller.plan?.volumeLimit
                            ? formatPriceForInput({
                                amount: seller.plan.volumeLimit,
                                currency: 'usd',
                                style: 'decimal',
                              })
                            : '',
                          customPrice: seller.plan?.customPrice
                            ? formatPriceForInput({
                                amount: seller.plan.customPrice,
                                currency: 'usd',
                                style: 'decimal',
                              })
                            : '',
                          contractStartDate: toContractDateInput(seller.plan?.contractStartDate),
                          contractEndDate: toContractDateInput(seller.plan?.contractEndDate),
                        },
                      }}
                      fields={[
                        {
                          name: 'plan.volumeLimit',
                          type: 'number',
                          label: 'Volume limit',
                          labelDescription: 'Maximum amount of revenue per year',
                          placeholder: '10000.00',
                          required: false,
                          step: 'any',
                          helpText: values =>
                            values.plan.volumeLimit
                              ? `${formatPrice({
                                  amount: formatPriceInput(values.plan.volumeLimit, 'usd'),
                                  currency: 'usd',
                                })}`
                              : null,
                        },
                        {
                          name: 'plan.customPrice',
                          type: 'number',
                          label: 'Custom price (per year)',
                          placeholder: '199.00',
                          required: false,
                          step: 'any',
                          helpText: values =>
                            values.plan.customPrice
                              ? `${formatPrice({
                                  amount: formatPriceInput(values.plan.customPrice, 'usd'),
                                  currency: 'usd',
                                })}`
                              : null,
                        },
                        {
                          name: 'plan.contractStartDate',
                          type: 'date',
                          label: 'Contract start',
                          required: false,
                        },
                        {
                          name: 'plan.contractEndDate',
                          type: 'date',
                          label: 'Contract end',
                          required: false,
                        },
                      ]}
                      onSubmit={async (values, actions) => {
                        try {
                          const submissionData = {
                            plan: {
                              ...seller.plan,
                              volumeLimit: values.plan.volumeLimit
                                ? formatPriceInput(values.plan.volumeLimit, 'usd')
                                : null,
                              customPrice: values.plan.customPrice
                                ? formatPriceInput(values.plan.customPrice, 'usd')
                                : null,
                              contractStartDate: fromContractDateInput(
                                values.plan.contractStartDate,
                              ),
                              contractEndDate: fromContractDateInput(values.plan.contractEndDate),
                            },
                          };

                          const data = await updateSellerMutation.mutateAsync({
                            sellerId,
                            data: submissionData,
                          });

                          actions.resetForm({
                            values: {
                              plan: {
                                volumeLimit: data.plan?.volumeLimit
                                  ? formatPriceForInput({
                                      amount: data.plan.volumeLimit,
                                      currency: 'usd',
                                      style: 'decimal',
                                    })
                                  : '',
                                customPrice: data.plan?.customPrice
                                  ? formatPriceForInput({
                                      amount: data.plan.customPrice,
                                      currency: 'usd',
                                      style: 'decimal',
                                    })
                                  : '',
                                contractStartDate: toContractDateInput(
                                  data.plan?.contractStartDate,
                                ),
                                contractEndDate: toContractDateInput(data.plan?.contractEndDate),
                              },
                            },
                          });

                          return {
                            data: {
                              plan: data.plan,
                            },
                          };
                        } catch (error) {
                          return { error };
                        }
                      }}
                      submitButton={{
                        label: 'Save',
                        appearance: 'secondary',
                        size: 'small',
                      }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          <MerchantAiSection sellerId={sellerId} />

          <div>
            <h2 className="mb-4">Actions</h2>

            <div className="space-y-8">
              <div className="">
                <Label htmlFor="subscriptionStatus">
                  Change subscription status: {seller.subscriptionStatus}
                  {getRenewalStatus(seller) === seller.subscriptionStatus
                    ? ''
                    : ` (${getRenewalStatus(seller)})`}
                </Label>

                <div className="space-x-2">
                  {adminUser.role === AdminRolesEnum.ADMIN && (
                    <Button
                      onClick={async () => {
                        const confirmed = await confirm(
                          'Are you sure you want to cancel this subscription?',
                        );

                        if (!confirmed) {
                          return;
                        }

                        cancelSubscriptionMutation.mutate({ sellerId });
                      }}
                      disabled={cancelSubscriptionMutation.isLoading}
                      appearance="secondary"
                      size="small"
                    >
                      Cancel subscription
                    </Button>
                  )}

                  <Button
                    onClick={async () => {
                      const confirmed = await confirm(
                        'Are you sure you want to expire this trial?',
                      );

                      if (!confirmed) {
                        return;
                      }

                      expireTrialMutation.mutate({ sellerId });
                    }}
                    disabled={expireTrialMutation.isLoading}
                    appearance="secondary"
                    size="small"
                  >
                    Expire trial
                  </Button>

                  {adminUser.role === AdminRolesEnum.ADMIN && (
                    <CompDialog
                      seller={seller}
                      sellerId={sellerId}
                      compSellerMutation={compSellerMutation}
                      removeCompMutation={removeCompMutation}
                    />
                  )}
                </div>
              </div>
            </div>

            <div className="mt-4">
              <Label>Extend trial</Label>

              <div className="flex gap-x-2">
                <div>
                  <Select
                    value={String(extendTrialDays)}
                    onValueChange={value => setExtendTrialDays(parseInt(value))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 day</SelectItem>
                      <SelectItem value="2">2 day</SelectItem>
                      <SelectItem value="3">3 days</SelectItem>
                      <SelectItem value="4">4 days</SelectItem>
                      <SelectItem value="5">5 days</SelectItem>
                      <SelectItem value="6">6 days</SelectItem>
                      <SelectItem value="7">7 days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button
                  onClick={async () => {
                    const confirmed = await confirm('Are you sure you want to extend this trial?');

                    if (!confirmed) {
                      return;
                    }

                    extendTrialMutation.mutate({ sellerId, days: extendTrialDays });
                  }}
                  disabled={extendTrialMutation.isLoading}
                  appearance="secondary"
                  size="small"
                >
                  Extend trial
                </Button>
              </div>
            </div>

            <div className="flex gap-x-2 mt-4">
              {adminUser.role === AdminRolesEnum.ADMIN && (
                <>
                  {seller.disabled ? (
                    <Button
                      onClick={() => enableSeller(seller._id)}
                      appearance="light"
                      size="small"
                    >
                      Enable store
                    </Button>
                  ) : (
                    <Button
                      onClick={() => disableSeller(seller._id)}
                      appearance="light"
                      size="small"
                    >
                      Disable store
                    </Button>
                  )}

                  <Button
                    handleClick={() => importStripeSubscriptions(seller._id)}
                    appearance="light"
                    disabled={!checkIsConnectedToStripe(seller)}
                    size="small"
                  >
                    Import Stripe Subscriptions
                  </Button>
                </>
              )}
            </div>

            {adminUser.role === AdminRolesEnum.ADMIN && (
              <>
                <div className="mt-4">
                  <Label>Aggregate historical statistics</Label>
                  <div className="flex gap-x-2">
                    <div>
                      <Select value={statisticsRange} onValueChange={setStatisticsRange}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="lastHour">Last hour</SelectItem>
                          {Object.keys(dateRanges).map(range => (
                            <SelectItem value={range} key={range}>
                              {dateRanges[range].label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Select
                        value={metric || 'all'}
                        onValueChange={value => setMetric(value === 'all' ? '' : value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All metrics</SelectItem>
                          {Object.entries(METRICS).map(([key, value]) => (
                            <SelectItem value={value} key={value}>
                              {upperFirst(lowerCase(key))}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <Button
                      onClick={() =>
                        startStatisticsAggregate.mutate({
                          sellerId,
                          range: statisticsRange,
                          metric,
                        })
                      }
                      appearance="secondary"
                      size="small"
                    >
                      Start statistics aggregation
                    </Button>
                  </div>
                </div>

                <div className="mt-4">
                  <Label>Aggregate historical metrics</Label>
                  <div className="flex gap-x-2">
                    <div>
                      <Select value={metricsRange} onValueChange={setMetricsRange}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="lastHour">Last hour</SelectItem>
                          {Object.keys(dateRanges).map(range => (
                            <SelectItem value={range} key={range}>
                              {dateRanges[range].label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <Button
                      onClick={() =>
                        startMetricsAggregation.mutate({ sellerId, range: metricsRange })
                      }
                      appearance="secondary"
                      size="small"
                    >
                      Start metrics aggregation
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>

          <div>
            <h2 className="mb-4">Feature flags</h2>

            <Form
              initialValues={{
                labs: {
                  slack: seller.labs?.slack,
                  linkInBio: seller.labs?.linkInBio || false,
                  betaApi: seller.labs?.betaApi || false,
                },
              }}
              fields={[
                {
                  name: 'labs.slack',
                  type: 'checkbox',
                  label: 'Slack integration',
                  required: false,
                },
                {
                  name: 'labs.linkInBio',
                  type: 'checkbox',
                  label: 'Link in bio',
                  required: false,
                },
                {
                  name: 'labs.betaApi',
                  type: 'checkbox',
                  label: 'API beta access',
                  required: false,
                },
              ]}
              onSubmit={async (values, actions) => {
                try {
                  const data = await updateSellerMutation.mutateAsync({ sellerId, data: values });

                  actions.resetForm({
                    values: {
                      labs: data.labs,
                    },
                  });

                  return {
                    data: {
                      labs: data.labs,
                    },
                  };
                } catch (error) {
                  return { error };
                }
              }}
              submitButton={{
                label: 'Save',
                appearance: 'secondary',
                size: 'small',
              }}
            />
          </div>
        </div>
      </div>

      <div>
        <h2 className="mb-4">Team members ({seller.users.length})</h2>
        <TeamMembersTable members={seller.users} sellerId={sellerId} />
      </div>

      <MerchantBillingHistory sellerId={sellerId} />
    </div>
  );
};

export default Merchant;
